import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users } from 'lucide-react';
import { HyperText } from "../magicui/hyper-text";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function HeroSection() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [isLive, setIsLive] = useState(false);

  // Memoize dates to prevent recreation on every render
  const { startDate, endDate } = useMemo(() => ({
    startDate: new Date('2025-07-10T00:00:00').getTime(),
    endDate: new Date('2025-08-13T23:59:59').getTime()
  }), []);

  // Optimized countdown calculation
  const calculateTimeLeft = useCallback((targetTime: number): TimeLeft => {
    const now = Date.now();
    const distance = targetTime - now;
    
    if (distance < 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000)
    };
  }, []);

  // Optimized timer effect with proper cleanup
  useEffect(() => {
    let intervalId: number;
    
    const updateTimer = () => {
      const currentTime = Date.now();
      
      // Check if program is live
      if (currentTime >= startDate && currentTime <= endDate) {
        setIsLive(true);
        return;
      }
      
      // Calculate countdown only if not live
      if (currentTime < startDate) {
        setIsLive(false);
        setTimeLeft(calculateTimeLeft(startDate));
      }
    };

    // Initial calculation
    updateTimer();
    
    // Use setInterval instead of requestAnimationFrame for lighter performance
    intervalId = window.setInterval(updateTimer, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [startDate, endDate, calculateTimeLeft]);

  // Memoized countdown items to prevent recreation
  const countdownItems = useMemo(() => [
    { label: 'Hari', value: timeLeft.days, unit: 'days' },
    { label: 'Jam', value: timeLeft.hours, unit: 'hours' },
    { label: 'Menit', value: timeLeft.minutes, unit: 'minutes' },
    { label: 'Detik', value: timeLeft.seconds, unit: 'seconds' }
  ], [timeLeft]);

  // Reduced floating elements for better performance
  const floatingElements = useMemo(() => 
    Array.from({ length: 3 }, (_, i) => ({
      id: i,
      size: 60 + i * 20,
      left: 20 + i * 30,
      top: 20 + i * 25,
      duration: 8 + i * 2
    }))
  , []);

  // Live state component - simplified animations
  const LiveComponent = useMemo(() => (
    <div className="relative min-h-screen bg-gradient-to-br from-green-50 via-white to-green-100 dark:from-green-900/20 dark:via-slate-900 dark:to-emerald-900/20 overflow-hidden flex items-center justify-center transition-colors duration-300 pt-20 sm:pt-22 lg:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center px-4 sm:px-6 lg:px-8"
      >
        <div className="h-24 w-24 sm:h-32 sm:w-32 bg-gradient-to-br from-green-500 to-green-600 dark:from-green-400 dark:to-green-500 rounded-2xl sm:rounded-3xl mx-auto mb-6 sm:mb-8 flex items-center justify-center shadow-xl">
          <span className="text-white font-bold text-2xl sm:text-4xl">KKN</span>
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-green-600 dark:text-green-400 mb-4">
          Program Dimulai!
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
          KKN UNSOED 2025 sedang berlangsung
        </p>
      </motion.div>
    </div>
  ), []);

  if (isLive) {
    return LiveComponent;
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 dark:from-slate-900 dark:via-slate-800 dark:to-blue-900/20 overflow-hidden transition-colors duration-300 pt-20 sm:pt-22 lg:pt-24">
      {/* Lighter Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Simplified floating circles */}
        {floatingElements.map((element) => (
          <motion.div
            key={element.id}
            className="absolute bg-blue-200/6 dark:bg-blue-400/3 rounded-full blur-xl"
            style={{
              width: element.size,
              height: element.size,
              left: `${element.left}%`,
              top: `${element.top}%`,
            }}
            animate={{
              y: [-5, 5, -5],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: element.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
        
        {/* Static gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/2 via-transparent to-blue-400/3 dark:from-blue-400/6 dark:via-transparent dark:to-purple-400/3" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-5rem)] sm:min-h-[calc(100vh-5.5rem)] lg:min-h-[calc(100vh-6rem)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-8 sm:py-12">
          
          {/* Title - Simplified animations */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-6 sm:mb-8"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-800 dark:text-white mb-4 transition-colors duration-300 leading-tight">
              <motion.span 
                className="block"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                KKN DESA
              </motion.span>
              <span className="block font-bold">
                <HyperText 
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight"
                  duration={1500}
                  startOnView={true}
                  animateOnHover={false}
                >
                  KARANGMALANG
                </HyperText>
              </span>
              <motion.span 
                className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-500 dark:from-blue-400 dark:to-teal-400 mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
              >
                2025
              </motion.span>
            </h1>
            <motion.div 
              className="h-1 bg-gradient-to-r from-blue-400 to-teal-400 dark:from-blue-500 dark:to-teal-500 rounded-full mx-auto mb-6 w-32 sm:w-48"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            />
          </motion.div>

          {/* Subtitle */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300"
          >
            UNIVERSITAS JENDERAL SOEDIRMAN
          </motion.h2>

          {/* Coming Soon Text */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="mb-8 sm:mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-100/80 dark:bg-blue-900/30 backdrop-blur-sm px-4 sm:px-6 py-2.5 sm:py-3 rounded-full border border-blue-200/50 dark:border-blue-700/50">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm sm:text-base">Coming Soon</span>
            </div>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto transition-colors duration-300 px-4">
              Program dimulai tanggal <br className="sm:hidden" />
              <strong>10 Juli - 13 Agustus 2025</strong>
            </p>
          </motion.div>

          {/* Countdown - Lighter animations */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mb-12 sm:mb-16"
          >
            {/* Main Countdown Container */}
            <div className="relative max-w-3xl mx-auto">
              {/* Digital Clock Display */}
              <div className="relative bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-slate-700/50 p-6 sm:p-8 shadow-lg">
                
                {/* Clock Numbers */}
                <div className="flex items-center justify-center space-x-3 sm:space-x-4 lg:space-x-6 mb-4">
                  {countdownItems.map((item, index) => (
                    <React.Fragment key={item.label}>
                      {/* Time Unit - Simplified animation */}
                      <div className="text-center">
                        <motion.div 
                          key={item.value}
                          initial={{ opacity: 0.7 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-mono font-bold text-gray-800 dark:text-white tracking-wide"
                        >
                          {String(item.value).padStart(2, '0')}
                        </motion.div>
                        <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">
                          {item.label}
                        </div>
                      </div>

                      {/* Separator */}
                      {index < countdownItems.length - 1 && (
                        <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-mono font-medium text-gray-400 dark:text-gray-500">
                          :
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Bottom Info */}
                <div className="text-center pt-3 border-t border-gray-200/50 dark:border-slate-700/50">
                  <div className="flex justify-center items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    <span>Program dimulai dalam</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {timeLeft.days > 0 ? `${timeLeft.days} hari lagi` : 'Segera dimulai!'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.4 }}
            className="hidden sm:block"
          >
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">Desa Karangmalang</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span className="text-sm">Tim KKN UNSOED</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">35 Hari Program</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - Simplified */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <motion.div
          animate={{ y: [0, 4, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-blue-300/50 dark:border-blue-400/50 rounded-full flex justify-center p-1 sm:p-2"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-1 h-2 sm:h-3 bg-blue-400 dark:bg-blue-300 rounded-full"
          />
        </motion.div>
      </motion.div>
    </div>
  );
}